import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, DollarSign, Building, FileText, Sparkles, ExternalLink, Hash, Globe, Download, Folder, Clock, Upload, Link, X, Trash2, Plus, Users, Cloud, Unlink } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const TenderDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [tender, setTender] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Document upload state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadType, setUploadType] = useState('file') // 'file', 'url', 'drag-drop'
  const [uploading, setUploading] = useState(false)
  const [urlInput, setUrlInput] = useState('')
  const [urlName, setUrlName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)
  
  // Lead linking state
  const [showLinkLeadModal, setShowLinkLeadModal] = useState(false)
  const [leads, setLeads] = useState([])
  const [leadSearchTerm, setLeadSearchTerm] = useState('')
  const [linkingLead, setLinkingLead] = useState(false)
  const [searchingLeads, setSearchingLeads] = useState(false)
  const [leadPage, setLeadPage] = useState(1)
  const [hasMoreLeads, setHasMoreLeads] = useState(false)
  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    fetchTenderDetails()
  }, [id])

  // Debounced search effect
  useEffect(() => {
    if (!showLinkLeadModal) return
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchLeadsFromZoho(leadSearchTerm, 1)
    }, 300)
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [leadSearchTerm, showLinkLeadModal])

  const fetchTenderDetails = async () => {
    try {
      const response = await api.get(`/tenders/${id}`)
      setTender(response.data)
    } catch (error) {
      console.error('Failed to fetch tender:', error)
    } finally {
      setLoading(false)
    }
  }

  const searchLeadsFromZoho = async (searchTerm, page = 1) => {
    setSearchingLeads(true)
    try {
      const response = await api.get('/crm/zoho/leads/search', {
        params: { q: searchTerm, page, limit: 20 }
      })
      if (page === 1) {
        setLeads(response.data.leads || [])
      } else {
        setLeads(prev => [...prev, ...(response.data.leads || [])])
      }
      setHasMoreLeads(response.data.hasMore || false)
      setLeadPage(page)
    } catch (error) {
      console.error('Failed to search leads:', error)
      toast.error('Failed to search leads from Zoho')
    } finally {
      setSearchingLeads(false)
    }
  }

  const loadMoreLeads = () => {
    if (!searchingLeads && hasMoreLeads) {
      searchLeadsFromZoho(leadSearchTerm, leadPage + 1)
    }
  }

  const handleLinkLead = async (lead) => {
    setLinkingLead(true)
    try {
      // Pass crmId for Zoho leads
      await api.post(`/tenders/${id}/link-lead`, { 
        leadId: lead._id || null,
        crmId: lead.crmId,
        leadData: {
          name: lead.name,
          email: lead.email,
          company: lead.company,
          phone: lead.phone
        }
      })
      toast.success('Lead linked successfully! Deal created in Zoho CRM.')
      setShowLinkLeadModal(false)
      fetchTenderDetails()
    } catch (error) {
      console.error('Failed to link lead:', error)
      toast.error(error.response?.data?.error || 'Failed to link lead')
    } finally {
      setLinkingLead(false)
    }
  }

  const handleUnlinkLead = async () => {
    if (!confirm('Are you sure you want to unlink this lead?')) return
    try {
      await api.delete(`/tenders/${id}/link-lead`)
      toast.success('Lead unlinked successfully')
      fetchTenderDetails()
    } catch (error) {
      console.error('Failed to unlink lead:', error)
      toast.error('Failed to unlink lead')
    }
  }

  const handleGenerateProposal = async () => {
    try {
      // Pass comprehensive tender context for auto-filling proposal form
      navigate('/proposals/generate', { 
        state: { 
          tenderId: tender._id,
          tenderData: {
            title: `Proposal for ${tender.title}`,
            organization: tender.organization,
            description: tender.description || '',
            budget: tender.estimatedValue ? `${tender.estimatedValue.currency || 'INR'} ${tender.estimatedValue.amount?.toLocaleString() || ''}` : '',
            timeline: tender.deadline ? `Deadline: ${format(new Date(tender.deadline), 'MMM dd, yyyy')}` : '',
            requirements: tender.aiAnalysis?.requirements?.join('\n') || '',
            category: tender.category || '',
            // Additional context for AI
            aiContext: {
              relevanceScore: tender.aiAnalysis?.relevanceScore,
              recommendation: tender.aiAnalysis?.recommendation,
              requirements: tender.aiAnalysis?.requirements || [],
              opportunities: tender.aiAnalysis?.opportunities || [],
              risks: tender.aiAnalysis?.risks || [],
              keyPoints: tender.aiAnalysis?.keyPoints || []
            },
            // Include tender documents for proposal generation
            documents: tender.documents || []
          }
        } 
      })
    } catch (error) {
      console.error('Failed to navigate:', error)
    }
  }

  // Document upload handlers
  const handleFileUpload = async (file, type = 'file') => {
    if (!file) return
    
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('document', file)
      formData.append('uploadType', type)
      
      const response = await api.post(`/tenders/${id}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      setTender(prev => ({
        ...prev,
        documents: [...(prev.documents || []), response.data.document]
      }))
      
      toast.success('Document uploaded successfully')
      setShowUploadModal(false)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload document')
    } finally {
      setUploading(false)
    }
  }

  const handleUrlUpload = async () => {
    if (!urlInput) {
      toast.error('Please enter a URL')
      return
    }
    
    setUploading(true)
    try {
      const response = await api.post(`/tenders/${id}/documents/upload-url`, {
        url: urlInput,
        name: urlName || undefined
      })
      
      setTender(prev => ({
        ...prev,
        documents: [...(prev.documents || []), response.data.document]
      }))
      
      if (response.data.warning) {
        toast.success(response.data.warning)
      } else {
        toast.success('Document uploaded from URL')
      }
      
      setUrlInput('')
      setUrlName('')
      setShowUploadModal(false)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload from URL')
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return
    
    try {
      await api.delete(`/tenders/${id}/documents/${docId}`)
      setTender(prev => ({
        ...prev,
        documents: prev.documents.filter(d => d._id !== docId)
      }))
      toast.success('Document deleted')
    } catch (error) {
      toast.error('Failed to delete document')
    }
  }

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileUpload(files[0], 'drag-drop')
    }
  }, [id])

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!tender) {
    return <div className="text-center py-12 text-gray-900 dark:text-gray-100">Tender not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/tenders')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-900 dark:text-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{tender.title}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{tender.organization}</p>
        </div>
        <button
          onClick={handleGenerateProposal}
          className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Generate Proposal
        </button>
      </div>

      {/* Key Info Badges */}
      <div className="flex flex-wrap gap-2">
        {(tender.gemBidId || tender.tenderNumber) && (
          <span className="inline-flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium">
            <Hash className="w-4 h-4 mr-1" />
            Bid: {tender.gemBidId || tender.tenderNumber}
          </span>
        )}
        {tender.source && (
          <span className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm">
            <Globe className="w-4 h-4 mr-1" />
            {tender.source}
          </span>
        )}
        {tender.category && (
          <span className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm">
            <Folder className="w-4 h-4 mr-1" />
            {tender.category}
          </span>
        )}
        {tender.department && (
          <span className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm">
            <Building className="w-4 h-4 mr-1" />
            {tender.department}
          </span>
        )}
        <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm ${
          tender.status === 'New' ? 'bg-blue-100 text-blue-700' :
          tender.status === 'Active' ? 'bg-green-100 text-green-700' :
          tender.status === 'Closed' ? 'bg-gray-100 text-gray-700' :
          'bg-yellow-100 text-yellow-700'
        }`}>
          {tender.status}
        </span>
      </div>

      {/* Tender Info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Release Date</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{tender.publishDate ? format(new Date(tender.publishDate), 'MMM dd, yyyy') : 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Deadline</p>
              <p className="font-semibold text-red-600 dark:text-red-400">{tender.deadline ? format(new Date(tender.deadline), 'MMM dd, yyyy') : 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Estimated Value</p>
              <p className="font-semibold text-green-600 dark:text-green-400">
                {tender.currency || 'INR'} {tender.estimatedValue ? 
                  (tender.estimatedValue >= 10000000 ? 
                    `${(tender.estimatedValue / 10000000).toFixed(2)} Cr` : 
                    `${(tender.estimatedValue / 100000).toFixed(2)} L`) 
                  : 'Not specified'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Building className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Organization</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{tender.organization}</p>
            </div>
          </div>
        </div>

        {/* Source URL */}
        {tender.sourceUrl && !tender.sourceUrl.includes('manual-entry') && !tender.sourceUrl.includes('bulk-upload') && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Source URL</p>
            <a 
              href={tender.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 dark:text-blue-400 hover:underline break-all"
            >
              <ExternalLink className="w-4 h-4 mr-2 flex-shrink-0" />
              {tender.sourceUrl}
            </a>
          </div>
        )}

        {tender.description && (
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</p>
            <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{tender.description}</p>
          </div>
        )}
      </div>

      {/* Linked Lead Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Users className="w-5 h-5" /> Linked Lead
          </h2>
          {!tender.linkedLead && (
            <button
              onClick={() => { setShowLinkLeadModal(true); fetchLeads(); }}
              className="flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              <Link className="w-4 h-4 mr-1" /> Link Lead
            </button>
          )}
        </div>
        
        {tender.linkedLead ? (
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <span className="text-primary-600 dark:text-primary-400 font-semibold">
                  {tender.linkedLead.name?.charAt(0) || 'L'}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{tender.linkedLead.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{tender.linkedLead.company} • {tender.linkedLead.email}</p>
              </div>
              {tender.linkedLead.crmId && (
                <span className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs">
                  <Cloud className="w-3 h-3 mr-1" /> Zoho Synced
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/leads/${tender.linkedLead._id}`)}
                className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 border border-primary-300 rounded-lg"
              >
                View Lead
              </button>
              <button
                onClick={handleUnlinkLead}
                className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                title="Unlink Lead"
              >
                <Unlink className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No lead linked to this tender. Link a lead to sync with Zoho CRM as a Deal.</p>
        )}
        
        {tender.zohoSyncStatus?.synced && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-700 dark:text-green-400">
              <Cloud className="w-4 h-4 inline mr-1" />
              Synced to Zoho CRM as Deal (ID: {tender.zohoSyncStatus.zohoDealId})
            </p>
            {tender.zohoSyncStatus.lastSyncedAt && (
              <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                Last synced: {format(new Date(tender.zohoSyncStatus.lastSyncedAt), 'MMM dd, yyyy HH:mm')}
              </p>
            )}
          </div>
        )}
        
        {tender.zohoSyncStatus?.error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">Sync Error: {tender.zohoSyncStatus.error}</p>
          </div>
        )}
      </div>

      {/* Tender Documents */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Tender Documents</h2>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Upload Document
          </button>
        </div>
        
        {tender.documents && tender.documents.length > 0 ? (
          <div className="space-y-3">
            {tender.documents.map((doc, index) => (
              <div key={doc._id || index} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    doc.fileType === 'PDF' ? 'bg-red-100 text-red-600' :
                    doc.fileType === 'DOCX' || doc.fileType === 'DOC' ? 'bg-blue-100 text-blue-600' :
                    doc.fileType === 'XLSX' || doc.fileType === 'XLS' ? 'bg-green-100 text-green-600' :
                    doc.fileType === 'URL' ? 'bg-purple-100 text-purple-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {doc.uploadType === 'url' && !doc.filePath ? <Link className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{doc.name || doc.originalName}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>{doc.fileType || 'Document'}</span>
                      <span>•</span>
                      <span>{formatFileSize(doc.fileSize)}</span>
                      {doc.uploadType && (
                        <>
                          <span>•</span>
                          <span className="capitalize">{doc.uploadType}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.url && (
                    <a
                      href={doc.url.startsWith('http') ? doc.url : `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}${doc.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-1.5 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {doc.uploadType === 'url' && !doc.filePath ? 'Open' : 'Download'}
                    </a>
                  )}
                  <button
                    onClick={() => handleDeleteDocument(doc._id)}
                    className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                    title="Delete document"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div 
            className={`text-center py-8 border-2 border-dashed rounded-lg transition-colors ${
              isDragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-500 dark:text-gray-400">No documents attached to this tender</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Drag & drop files here or click "Upload Document" to add
            </p>
          </div>
        )}
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload Tender Document
              </h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Upload Type Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
              <button
                onClick={() => setUploadType('file')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  uploadType === 'file' 
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                File Upload
              </button>
              <button
                onClick={() => setUploadType('url')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  uploadType === 'url' 
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Link className="w-4 h-4 inline mr-2" />
                From URL
              </button>
              <button
                onClick={() => setUploadType('drag-drop')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  uploadType === 'drag-drop' 
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Drag & Drop
              </button>
            </div>

            {/* File Upload */}
            {uploadType === 'file' && (
              <div className="space-y-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileUpload(e.target.files[0], 'file')}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                >
                  <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400">Click to select a file</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG (max 25MB)
                  </p>
                </button>
              </div>
            )}

            {/* URL Upload */}
            {uploadType === 'url' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Document URL *
                  </label>
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/document.pdf"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Document Name (optional)
                  </label>
                  <input
                    type="text"
                    value={urlName}
                    onChange={(e) => setUrlName(e.target.value)}
                    placeholder="e.g., Tender Specifications"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <button
                  onClick={handleUrlUpload}
                  disabled={uploading || !urlInput}
                  className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <Link className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Import from URL'}
                </button>
              </div>
            )}

            {/* Drag & Drop */}
            {uploadType === 'drag-drop' && (
              <div
                className={`py-16 border-2 border-dashed rounded-lg transition-colors ${
                  isDragging 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  handleDrop(e)
                  setShowUploadModal(false)
                }}
              >
                <Upload className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
                <p className={`text-center ${isDragging ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {isDragging ? 'Drop your file here!' : 'Drag and drop your file here'}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
                  PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG (max 25MB)
                </p>
              </div>
            )}

            {uploading && (
              <div className="mt-4 flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400">
                <div className="spinner w-5 h-5"></div>
                <span>Uploading document...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Analysis */}
      {tender.aiAnalysis && (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-sm border border-blue-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">AI Analysis</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Relevance Score</p>
              <p className="text-3xl font-bold text-blue-600">{tender.aiAnalysis.relevanceScore || 0}%</p>
            </div>
            
            {tender.aiAnalysis.recommendation && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Recommendation</p>
                <p className="text-gray-900">{tender.aiAnalysis.recommendation}</p>
              </div>
            )}

            {tender.aiAnalysis.requirements && tender.aiAnalysis.requirements.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-700 mb-2">Key Requirements</p>
                <ul className="list-disc list-inside space-y-1">
                  {tender.aiAnalysis.requirements.map((req, i) => (
                    <li key={i} className="text-gray-600">{req}</li>
                  ))}
                </ul>
              </div>
            )}

            {tender.aiAnalysis.opportunities && tender.aiAnalysis.opportunities.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Opportunities</p>
                <ul className="list-disc list-inside space-y-1">
                  {tender.aiAnalysis.opportunities.map((opp, i) => (
                    <li key={i} className="text-green-600">{opp}</li>
                  ))}
                </ul>
              </div>
            )}

            {tender.aiAnalysis.risks && tender.aiAnalysis.risks.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Risks</p>
                <ul className="list-disc list-inside space-y-1">
                  {tender.aiAnalysis.risks.map((risk, i) => (
                    <li key={i} className="text-red-600">{risk}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Related Proposals */}
      {tender.proposals && tender.proposals.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Related Proposals</h2>
          <div className="space-y-3">
            {tender.proposals.map((proposal) => (
              <div 
                key={proposal._id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/proposals/${proposal._id}`)}
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-semibold text-gray-900">{proposal.title}</p>
                    <p className="text-sm text-gray-500">Version {proposal.version}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  proposal.status === 'Approved' ? 'bg-green-100 text-green-700' :
                  proposal.status === 'Draft' ? 'bg-gray-100 text-gray-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {proposal.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link Lead Modal */}
      {showLinkLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Link Lead from Zoho CRM</h3>
              <button onClick={() => { setShowLinkLeadModal(false); setLeadSearchTerm(''); setLeads([]); }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type to search leads in Zoho CRM..."
                  value={leadSearchTerm}
                  onChange={(e) => setLeadSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  autoFocus
                />
                {searchingLeads && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
              {searchingLeads && leads.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">Searching Zoho CRM...</span>
                </div>
              ) : leads.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {leadSearchTerm ? 'No leads found matching your search' : 'Start typing to search leads in Zoho CRM'}
                </p>
              ) : (
                <div className="space-y-2">
                  {leads.map((lead, index) => (
                    <div
                      key={lead.crmId || index}
                      onClick={() => !linkingLead && handleLinkLead(lead)}
                      className={`flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${linkingLead ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                          <span className="text-primary-600 dark:text-primary-400 text-sm font-semibold">
                            {lead.name?.charAt(0) || 'L'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{lead.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{lead.company} • {lead.email}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs">
                        <Cloud className="w-3 h-3 mr-1" /> Zoho
                      </span>
                    </div>
                  ))}
                  
                  {hasMoreLeads && (
                    <button
                      onClick={loadMoreLeads}
                      disabled={searchingLeads}
                      className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      {searchingLeads ? 'Loading...' : 'Load More'}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-xl">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Search and select a lead from Zoho CRM. A Deal will be created automatically.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TenderDetails
